import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ProveedorDeAvisos } from '@fialrest/ui'
import '@fialrest/ui/tipografias.css'
import '@fialrest/ui/tokens.css'
import '@fialrest/ui/componentes.css'
// Después de los del sistema: la capa base del documento los da por puestos.
import './base.css'
import { Armazon } from './pantallas/Armazon.js'
import { Entrar } from './pantallas/Entrar.js'
import { SeccionDeModulo } from './pantallas/SeccionDeModulo.js'
import { SeccionConPermiso } from './pantallas/SeccionConPermiso.js'
import {
  PANTALLA_DE_APARATOS, PANTALLA_DE_EXTRAS, PANTALLA_DE_FOTOGRAFIAS,
  PANTALLA_DE_MI_CAFETERIA, PANTALLA_DE_PERSONAL,
  PANTALLA_DE_TAMANOS,
} from './modulos/registro.js'

/**
 * T-09 · El panel.
 *
 * Las rutas no llevan datos: los pide `useYo()` una vez, en el armazón, y las
 * secciones lo reciben por contexto. Pedirlo en cada pantalla serían N
 * peticiones para la misma respuesta.
 */
const clienteDeConsultas = new QueryClient({
  defaultOptions: {
    queries: {
      // Un 401 no se reintenta: insistir con una cookie que no vale sólo retrasa
      // la pantalla de entrada. Cada consulta afina lo suyo.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const rutas = createBrowserRouter([
  { path: '/entrar', element: <Entrar /> },
  {
    path: '/',
    element: <Armazon />,
    children: [
      { index: true, element: <Navigate to="/menu" replace /> },
      // T-22 · Las secciones de módulo se cargan bajo demanda. `Seccion` se
      // queda para las que no dependen de nada que se venda por separado.
      // El título de cada sección lo pone el armazón en su barra superior
      // (`T-36`), y sale de `SECCIONES`. Aquí sólo se dice qué se monta.
      { path: 'menu', element: <SeccionDeModulo modulo="menu" /> },
      // `T-37` · Fotografías es la **segunda sección del módulo `menu`**: pasa
      // por la misma puerta que el armador —sin el módulo no se monta— y sólo
      // cambia qué pantalla hay detrás.
      {
        path: 'fotografias',
        element: <SeccionDeModulo modulo="menu" pantalla={PANTALLA_DE_FOTOGRAFIAS} />,
      },
      // `T-41` · Y la tercera del módulo `menu`: el catálogo de extras. Misma
      // puerta que las dos de arriba — sin el módulo no se monta.
      {
        path: 'extras',
        element: <SeccionDeModulo modulo="menu" pantalla={PANTALLA_DE_EXTRAS} />,
      },
      // `T-43` · Y la cuarta: las plantillas de tamaños. Misma puerta.
      {
        path: 'tamanos',
        element: <SeccionDeModulo modulo="menu" pantalla={PANTALLA_DE_TAMANOS} />,
      },
      { path: 'lealtad', element: <SeccionDeModulo modulo="loyalty" /> },
      //  `T-65` · Detrás de su permiso, y por la URL también. Esconder el
      //  enlace en la barra no cierra `/personal` tecleado a mano, y ahí una
      //  cajera vería la lista pedirse, fallar con 403 y pintar un error de red
      //  donde tocaba una explicación. Ver `SeccionConPermiso`.
      {
        path: 'personal',
        element: <SeccionConPermiso permiso="staff.manage" pantalla={PANTALLA_DE_PERSONAL} />,
      },
      {
        path: 'aparatos',
        element: <SeccionConPermiso permiso="device.revoke" pantalla={PANTALLA_DE_APARATOS} />,
      },
      //  `T-49` · «Mi cafetería» — `ADR-029`. Sin `SeccionDeModulo` y es la
      //  decisión: «el panel gana su primera sección que NO pertenece a un
      //  módulo: se ve siempre, con menú o sin él».
      {
        path: 'mi-cafeteria',
        element: (
          <Suspense fallback={<p aria-busy="true">Cargando…</p>}>
            <PANTALLA_DE_MI_CAFETERIA />
          </Suspense>
        ),
      },
    ],
  },
  // Cualquier otra cosa va al panel, que decide si hay sesión. Una pantalla de
  // «no encontrado» aquí obligaría a decidir sin saber quién pregunta.
  { path: '*', element: <Navigate to="/" replace /> },
])

const raiz = document.getElementById('raiz')
if (!raiz) throw new Error('Falta el nodo #raiz')

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={clienteDeConsultas}>
      {/* `T-42` · Los avisos flotantes viven aquí arriba y no en una pantalla,
          por dos razones: la región `aria-live` tiene que existir en el árbol
          ANTES de que llegue lo que anuncia —si no, el lector no lo lee— y un
          acuse tiene que sobrevivir a que se cierre el diálogo que lo provocó.

          Sin nadie que avise no pinta nada: es una región vacía. Hoy sólo la usa
          el catálogo de extras. */}
      <ProveedorDeAvisos>
        <RouterProvider router={rutas} />
      </ProveedorDeAvisos>
    </QueryClientProvider>
  </StrictMode>,
)
